// src/pages/OrdersPage.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import OrderDetailModal from "../components/modals/OrderDetailmodal";
import EscrowWithdrawalModal from "../components/modals/EscrowWithdrawalModal";
import api from "../utils/api";
import { API_CONFIG } from "../utils/apiConfig";
import { showToast } from "../utils/helpers";

const OrdersPage = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showEscrowWithdrawalModal, setShowEscrowWithdrawalModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showEscrowInfo, setShowEscrowInfo] = useState(false);
  const [ordersData, setOrdersData] = useState({
    orders: [],
    totalOrders: 0,
    totalDeliveredAmount: 0,
    totalPendingAmount: 0,
    availableBalance: 0,
    currency: 'NGN'
  });
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [currentLimit, setCurrentLimit] = useState(10);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const loadMoreRef = useRef();

  // Memoized orders array
  const safeOrders = useMemo(() => 
    Array.isArray(ordersData.orders) ? ordersData.orders : [],
    [ordersData.orders]
  );

  // Pagination data
  const {
    current_page = 1,
    total_pages = 1,
    total_orders = 0,
    orders_per_page = 10,
    has_previous = false,
    has_next = false
  } = pagination;

  // Infinite scroll setup
  useEffect(() => {
    if (!has_next || isLoading || isLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [has_next, isLoading, isLoadingMore]);

  // Fetch orders data from API with pagination
  const fetchOrders = useCallback(async (page = currentPage, limit = currentLimit, append = false) => {
    try {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      const response = await api.get(`${API_CONFIG.ORDERS.STORE_ORDERS}?page=${page}&limit=${limit}`);
      
      if (response.data.success) {
        if (append) {
          // Append new orders to existing ones
          setOrdersData(prev => ({
            ...prev,
            orders: [...prev.orders, ...(response.data.myOrders || [])]
          }));
        } else {
          // Replace orders for new page
          setOrdersData({
            orders: response.data.myOrders || [],
            totalOrders: response.data.totalOrders || 0,
            totalDeliveredAmount: response.data.totalDeliveredAmount || 0,
            totalPendingAmount: response.data.totalPendingAmount || 0,
            availableBalance: response.data.availableBalance || 0,
            currency: response.data.currency || 'NGN'
          });
        }
        
        setPagination(response.data.pagination || {});
      } else {
        setOrdersData({
          orders: [],
          totalOrders: 0,
          totalDeliveredAmount: 0,
          totalPendingAmount: 0,
          availableBalance: 0,
          currency: 'NGN'
        });
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrdersData({
        orders: [],
        totalOrders: 0,
        totalDeliveredAmount: 0,
        totalPendingAmount: 0,
        availableBalance: 0,
        currency: 'NGN'
      });
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [currentPage, currentLimit]);

  // Handle loading more orders
  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !has_next) return;
    
    setIsLoadingMore(true);
    try {
      await fetchOrders(current_page + 1, currentLimit, true);
    } catch (error) {
      console.error('Error loading more orders:', error);
      showToast('Failed to load more orders', 'error');
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, has_next, current_page, currentLimit, fetchOrders]);

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  // Handle limit change
  const handleLimitChange = useCallback((newLimit) => {
    setCurrentLimit(newLimit);
    setCurrentPage(1); // Reset to first page when changing limit
  }, []);

  // Initial data load
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Reload when page or limit changes
  useEffect(() => {
    if (currentPage > 1) {
      fetchOrders(currentPage, currentLimit, false);
    }
  }, [currentPage, currentLimit, fetchOrders]);

  const { totalOrders, totalDeliveredAmount, totalPendingAmount, availableBalance, currency } = ordersData;

  // Skeleton Loader Components
  const SkeletonOrderRow = () => (
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center bg-white p-4 rounded-lg animate-pulse border border-gray-200">
      <div className="flex-1 mb-3 sm:mb-0">
        <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-48 mb-2"></div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-y-1 gap-x-4">
          <div className="h-3 bg-gray-200 rounded w-24"></div>
          <div className="h-3 bg-gray-200 rounded w-32"></div>
          <div className="h-3 bg-gray-200 rounded w-20"></div>
        </div>
      </div>
      <div className="flex justify-between items-center sm:flex-col sm:items-end gap-2 pt-3 border-t sm:border-t-0 border-gray-200 w-full sm:w-auto">
        <div className="h-4 bg-gray-200 rounded w-16"></div>
        <div className="h-6 bg-gray-200 rounded w-20"></div>
      </div>
    </div>
  );

  // Pagination Controls Component
  const PaginationControls = () => {
    if (total_pages <= 1) return null;

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0 px-4 py-4 border-t border-gray-200  mt-6">
        {/* Items per page selector */}
        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-700">Show:</span>
          <select
            value={orders_per_page}
            onChange={(e) => handleLimitChange(parseInt(e.target.value))}
            className="border border-gray-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className="text-xs text-gray-700">per page</span>
        </div>

        {/* Page info */}
        <div className="text-xs text-gray-700">
          Showing {(current_page - 1) * orders_per_page + 1} to{' '}
          {Math.min(current_page * orders_per_page, total_orders)} of{' '}
          {total_orders.toLocaleString()} orders
        </div>

        {/* Page navigation */}
        <div className="flex items-center space-x-1">
          <button
            onClick={() => handlePageChange(current_page - 1)}
            disabled={!has_previous || isLoading}
            className="px-2 py-1 border text-gray-500 border-gray-300 rounded-md text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed hover: transition-colors"
          >
            Previous
          </button>
          
          {/* Page numbers */}
          {Array.from({ length: Math.min(5, total_pages) }, (_, i) => {
            let pageNum;
            if (total_pages <= 5) {
              pageNum = i + 1;
            } else if (current_page <= 3) {
              pageNum = i + 1;
            } else if (current_page >= total_pages - 2) {
              pageNum = total_pages - 4 + i;
            } else {
              pageNum = current_page - 2 + i;
            }

            return (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                disabled={isLoading}
                className={`px-2 py-1 border text-xs font-medium transition-colors ${
                  current_page === pageNum
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'border-gray-300 text-gray-700 hover:'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            onClick={() => handlePageChange(current_page + 1)}
            disabled={!has_next || isLoading}
            className="px-2 py-1 text-gray-500 border border-gray-300 rounded-md text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed hover: transition-colors"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  // Load More Component for Infinite Scroll
  const LoadMoreTrigger = () => {
    if (!has_next) return null;

    return (
      <div ref={loadMoreRef} className="py-6 text-center">
        {isLoadingMore ? (
          <div className="flex items-center justify-center space-x-2 text-gray-600">
            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-xs">Loading more orders...</span>
          </div>
        ) : (
          <button
            onClick={handleLoadMore}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs"
          >
            Load More
          </button>
        )}
      </div>
    );
  };

  // Rest of your existing helper functions remain the same...
  const formatCurrency = (amount, curr = currency) => {
    if (!amount) return curr === 'NGN' ? '₦0.00' : '$0.00';
    
    try {
      const options = {
        style: 'currency',
        currency: curr,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      };
      
      return new Intl.NumberFormat(curr === 'NGN' ? 'en-NG' : 'en-US', options).format(amount);
    } catch (error) {
      return curr === 'NGN' ? `₦${amount.toFixed(2)}` : `$${amount.toFixed(2)}`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
      case 'delivered':
      case 'completed':
        return "bg-green-100 text-green-800 text-xs";
      case 'processing':
      case 'pending':
      case 'payment_pending':
        return "bg-yellow-100 text-yellow-800 text-xs";
      case 'shipped':
      case 'scheduled':
        return "bg-blue-100 text-blue-800 text-xs";
      case 'cancelled':
      case 'failed':
        return "bg-red-100 text-red-800 text-xs";
      case 'returned':
        return "bg-purple-100 text-purple-800 text-xs";
      case 'accepted':
        return "bg-green-50 text-green-700 text-xs";
      default:
        return "bg-gray-100  text-xs";
    }
  };

  const getOrderTypeBadge = (orderType) => {
    if (orderType?.toLowerCase() === 'service') {
      return "bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full";
    }
    return "bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full";
  };

  const getOrderIcon = (orderType) => {
    return orderType?.toLowerCase() === 'service' ? '🔧' : '📦';
  };

  const filteredOrders = useMemo(() => {
    return activeTab === "all"
      ? safeOrders
      : safeOrders.filter(order => order.order_status?.toLowerCase() === activeTab.toLowerCase());
  }, [activeTab, safeOrders]);

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  const closeOrderModal = () => {
    setShowOrderModal(false);
    setSelectedOrder(null);
  };

  const closeEscrowWithdrawalModal = () => {
    setShowEscrowWithdrawalModal(false);
  };

  const refreshOrdersData = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      const response = await api.put(`${API_CONFIG.ORDERS.UPDATE_ORDER_STATUS}/${orderId}/status`, {
        status: newStatus
      });
      
      if (response.data.success) {
        showToast('Order status updated successfully!', 'success');
        refreshOrdersData();
        
        if (selectedOrder && selectedOrder.order_id === orderId) {
          setSelectedOrder({ ...selectedOrder, order_status: newStatus });
        }
        
        return true;
      } else {
        showToast('Failed to update order status', 'error');
        return false;
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      showToast(`Error updating order status: ${error.response?.data?.message || error.message}`, 'error');
      return false;
    }
  };

  const handleManualRefresh = () => {
    setIsLoading(true);
    refreshOrdersData();
  };

  const tabsRef = useRef(null);

  // Scroll active tab into view
  useEffect(() => {
    if (!tabsRef.current) return;
    const activeBtn = tabsRef.current.querySelector(`[data-tab-id="${activeTab}"]`);
    if (activeBtn) {
      activeBtn.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [activeTab]);

  // Tab configuration with service-specific statuses
  const tabs = [
    { id: 'all', label: 'All', fullLabel: 'All Orders' },
    { id: 'processing', label: 'Proc.', fullLabel: 'Processing' },
    { id: 'payment_pending', label: 'Payment', fullLabel: 'Payment Pending' },
    { id: 'pending', label: 'Pending', fullLabel: 'Pending' },
    { id: 'accepted', label: 'Accepted', fullLabel: 'Accepted' },
    { id: 'scheduled', label: 'Scheduled', fullLabel: 'Scheduled' },
    { id: 'shipped', label: 'Ship.', fullLabel: 'Shipped' },
    { id: 'delivered', label: 'Delivered', fullLabel: 'Delivered' },
    { id: 'paid', label: 'Paid', fullLabel: 'Paid' },
    { id: 'completed', label: 'Completed', fullLabel: 'Completed' },
    { id: 'cancelled', label: 'Cancel.', fullLabel: 'Cancelled' },
    { id: 'returned', label: 'Return.', fullLabel: 'Returned' }
  ];

  if (isLoading && safeOrders.length === 0) {
    return (
      <div className="md:w-full w-full max-w-[100vw] mt-2 m-auto bg-white rounded-lg">
       <div className="border-b border-gray-200">

          <div className="w-full overflow-x-auto scrollbar-hide">

            <div className="inline-flex flex-nowrap min-w-full px-3 py-2">

              {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (

                <div key={i} className="flex-shrink-0 flex items-center px-3 py-2 animate-pulse">

                  <div className="h-4 bg-gray-200 rounded w-16 sm:w-20"></div>

                </div>

              ))}

            </div>

          </div>

        </div>


        {/* Content Skeleton */}

        <div className="p-4">

          <div className="flex justify-between items-center mb-4">

            <div className="h-5 bg-gray-200 rounded w-32 animate-pulse"></div>

            <div className="h-8 bg-gray-200 rounded w-20 animate-pulse"></div>

          </div>

          

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">

            {[1, 2, 3, 4].map(i => (

              <div key={i} className=" p-3 rounded-lg animate-pulse">

                <div className="h-3 bg-gray-200 rounded w-3/4 mb-2"></div>

                <div className="h-4 bg-gray-200 rounded w-1/2"></div>

              </div>

            ))}

          </div>

          

          <div className="space-y-3">

            {[1, 2, 3, 4, 5].map(i => (

              <div key={i} className="flex justify-between items-center  p-3 rounded-lg animate-pulse">

                <div className="flex-1">

                  <div className="h-3 bg-gray-200 rounded w-24 mb-2"></div>

                  <div className="h-3 bg-gray-200 rounded w-32"></div>

                </div>

                <div className="text-right">

                  <div className="h-3 bg-gray-200 rounded w-16 mb-2"></div>

                  <div className="h-3 bg-gray-200 rounded w-12"></div>

                </div>

              </div>

            ))}

          </div>

        </div>

      </div>
    );
  }

  return (
    <div className="md:w-[100%] w-[100%] max-w-[100%] m-auto bg-white rounded-lg mt-2 shadow-sm pb-[100px]">
      
      {/* Tab Navigation - Horizontally Scrollable */}
      <motion.div
        initial={{ x: -60, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="border-b border-gray-200 bg-white rounded-t-lg"
      >
        <div className="w-[100%] sm:w-full md:w-full xl:w-full m-auto overflow-x-auto scrollbar-hide">
          <div ref={tabsRef} className="inline-flex flex-nowrap min-w-max px-3 py-2 space-x-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                data-tab-id={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-shrink-0 flex items-center px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap border-b-2 ${
                  activeTab === tab.id
                    ? "text-blue-600 border-blue-600"
                    : "text-gray-500 hover:text-gray-700 border-transparent"
                }`}
                title={tab.fullLabel}
              >
                <span className="text-xs sm:text-sm">
                  <span className="inline sm:hidden">{tab.label}</span> 
                  <span className="hidden sm:inline">{tab.fullLabel}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Main Content Area */}
      <div className="w-[100%] p-4 rounded-b-lg">
        
        {/* Header with Refresh Button */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6 w-[100%]">
          <div>
            <h1 className="text-md font-semibold text-gray-900">Order Management</h1>
            <p className="text-sm text-gray-500 mt-1">
              {isLoading ? 'Loading orders...' : `Showing ${safeOrders.length} of ${total_orders.toLocaleString()} orders`}
            </p>
          </div>
          <button
            onClick={handleManualRefresh}
            className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm w-[100%] sm:w-auto shadow-md"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh Data
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 w-[100%]">
          <div className=" p-4 rounded-lg border border-gray-200 shadow-sm">
            <h6 className="text-xs text-gray-600 font-medium mb-1">Total Orders</h6>
            <p className="text-lg font-bold text-gray-900">{total_orders.toLocaleString()}</p>
          </div>
          <div className=" p-4 rounded-lg border border-gray-200 shadow-sm">
            <h6 className="text-xs text-gray-600 font-medium mb-1">Delivered Value</h6>
            <p className="text-lg font-bold text-green-600">
              {formatCurrency(totalDeliveredAmount)}
            </p>
          </div>
          <div className=" p-4 rounded-lg border border-gray-200 shadow-sm">
            <h6 className="text-xs text-gray-600 font-medium mb-1">Pending Value</h6>
            <p className="text-lg font-bold text-yellow-600">
              {formatCurrency(totalPendingAmount)}
            </p>
          </div>
          <div className=" p-4 rounded-lg border border-gray-200 shadow-sm flex flex-col justify-between">
            <div>
              <h6 className="text-xs text-gray-600 font-medium mb-1">Available Balance</h6>
              <p className="text-lg font-bold text-blue-600">
                {formatCurrency(availableBalance)}
              </p>
            </div>
            <div className="mt-2 flex justify-between items-center">
                <button
                onClick={() => setShowEscrowInfo(true)}
                className="text-xs text-blue-500 hover:text-blue-700 underline transition-colors"
                >
                Escrow Info
                </button>
                {availableBalance > 0 && (
                    <button
                        onClick={() => setShowEscrowWithdrawalModal(true)}
                        className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors font-medium shadow-sm"
                    >
                        Withdraw
                    </button>
                )}
            </div>
          </div>
        </div>

        {/* Orders List */}
        <div className="mt-8 w-[100%]">
            <h2 className="text-lg font-semibold  mb-4">
                {tabs.find(t => t.id === activeTab)?.fullLabel || 'Orders'}
            </h2>
            {filteredOrders.length === 0 ? (
            <div className=" p-6 rounded-lg text-center border border-gray-200">
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                </div>
                <p className="text-sm text-gray-600 font-medium">No orders found</p>
                <p className="text-xs text-gray-500 mt-1">
                {activeTab === "all" 
                    ? "You don't have any orders yet" 
                    : `No ${activeTab.replace('_', ' ')} orders found`}
                </p>
            </div>
            ) : (
            <div className="space-y-3 w-[100%]">
                {filteredOrders.map((order) => (
                <div
                    key={order.order_id}
                    onClick={() => handleOrderClick(order)}
                    className="flex flex-col sm:flex-row sm:justify-between sm:items-center bg-white p-4 rounded-lg hover:shadow-lg transition-all cursor-pointer border border-gray-200 hover:border-blue-300 shadow-sm w-[100%]"
                >
                    {/* Left Section - Order Info */}
                    <div className="flex-1 mb-3 sm:mb-0 sm:mr-4 w-[100%]">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-sm">{getOrderIcon(order.order_type)}</span>
                              <p className="text-sm font-semibold text-gray-900 truncate max-w-full">
                                {order.order_type === 'service' ? 'Service Booking' : 'Order'} #{order.order_id}
                              </p>
                            </div>
                            <span className={getOrderTypeBadge(order.order_type)}>
                              {order.order_type === 'service' ? 'Service' : 'Product'}
                            </span>
                            <span className={`px-2 py-1 rounded-full font-medium ${getStatusBadgeClass(order.order_status)}`}>
                            {order.order_status || 'Unknown'}
                            </span>
                            <span className={`px-2 py-1 rounded-full font-medium ${getStatusBadgeClass(order.payment_status)}`}>
                            {order.payment_status || 'Unknown'}
                            </span>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-y-1 gap-x-4 text-xs text-gray-600">
                            <p className="truncate">
                                <span className="font-medium">Customer:</span> {order.customer_name || 'Unknown'}
                            </p>
                            <p className="truncate">
                                <span className="font-medium">Date:</span> {formatDate(order.created_at)}
                            </p>
                            {order.order_type === 'service' ? (
                              <p className="truncate">
                                <span className="font-medium">Type:</span> Service Booking
                              </p>
                            ) : (
                              <p className="truncate">
                                <span className="font-medium">Items:</span> {order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''}
                              </p>
                            )}
                        </div>

                        {/* Service-specific information */}
                        {order.order_type === 'service' && order.service_details && (
                          <div className="mt-2 text-xs text-gray-600">
                            <p className="truncate">
                              <span className="font-medium">Project:</span> {order.service_details.project_title || 'No title'}
                            </p>
                          </div>
                        )}
                    </div>

                    {/* Right Section - Amount and Actions */}
                    <div className="flex justify-between items-center sm:flex-col sm:items-end sm:justify-center gap-2 pt-3 border-t sm:border-t-0 border-gray-200 w-[100%] sm:w-auto">
                        <p className="text-sm font-bold text-gray-900">
                            {formatCurrency(order.total_amount)}
                        </p>
                        <span className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs font-medium hover:bg-blue-100 transition-colors">
                            View Details
                        </span>
                    </div>
                </div>
                ))}
                
                {/* Additional skeleton rows when loading more */}
                {isLoadingMore && Array.from({ length: 3 }, (_, i) => (
                  <SkeletonOrderRow key={`skeleton-${i}`} />
                ))}
            </div>
            )}
            
            {/* Load More Trigger for Infinite Scroll */}
            <LoadMoreTrigger />
        </div>

        {/* Pagination Controls */}
        <PaginationControls />
      </div>

      {/* Your existing modals remain the same */}
    {showEscrowInfo && (

        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">

          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-auto">

            <div className="flex justify-between items-center p-4 border-b border-gray-200">

              <h2 className="text-lg font-semibold text-gray-900">Escrow Protection</h2>

              <button 

                onClick={() => setShowEscrowInfo(false)}

                className="text-gray-400 hover:text-gray-600 transition-colors p-1"

              >

                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">

                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />

                </svg>

              </button>

            </div>

            

            <div className="p-4">

              <div className="flex items-start mb-4">

                <div className="bg-blue-100 p-2 rounded-full mr-3 flex-shrink-0">

                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">

                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />

                  </svg>

                </div>

                <div>

                  <h3 className="text-sm font-semibold ">Funds Held in Escrow</h3>

                  <p className="text-xs text-gray-600 mt-1">

                    Your funds are securely held in escrow until customers confirm delivery.

                  </p>

                </div>

              </div>


              <div className="space-y-2 text-xs text-gray-600 mb-4">

                <div className="flex items-start">

                  <span className="text-green-500 mr-2 mt-0.5">✓</span>

                  <span>Funds released when customers confirm successful delivery</span>

                </div>

                <div className="flex items-start">

                  <span className="text-green-500 mr-2 mt-0.5">✓</span>

                  <span>You'll receive notifications when funds are available</span>

                </div>

                <div className="flex items-start">

                  <span className="text-green-500 mr-2 mt-0.5">✓</span>

                  <span>Withdrawals available once funds are in your wallet balance</span>

                </div>

              </div>


              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">

                <p className="text-xs text-blue-700">

                  <strong className="font-semibold">Note:</strong> This escrow system protects both you and your customers, ensuring payments are only released after successful deliveries.

                </p>

              </div>

            </div>


            <div className="flex justify-end p-4 border-t border-gray-200">

              <button

                onClick={() => setShowEscrowInfo(false)}

                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors font-medium"

              >

                I Understand

              </button>

            </div>

          </div>

        </div>

      )}

      {/* Modals */}
      <OrderDetailModal
        show={showOrderModal}
        onHide={closeOrderModal}
        order={selectedOrder}
        currency={currency}
        onStatusUpdate={handleStatusUpdate}
        onOrderUpdated={refreshOrdersData}
      />
      
      {showEscrowWithdrawalModal && (
        <EscrowWithdrawalModal
          show={showEscrowWithdrawalModal}
          onHide={closeEscrowWithdrawalModal}
          availableBalance={availableBalance}
          currency={currency}
          onWithdrawSuccess={refreshOrdersData}
        />
      )}
    </div>
  );
};

export default OrdersPage;